import { invokeFunction } from '@/lib/supabase';

export type ProfileImageTargetType = 'user' | 'household' | 'child';

export interface UploadAvatarImageResult {
  avatar_image_path: string;
  signed_url: string | null;
  expires_in: number;
}

export interface SignedUrlBatchResult {
  urls: Record<string, string | null>;
  expires_in: number;
}

const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // mirrors edge function limit
const TARGET_MAX_DIMENSION = 512;

/**
 * Upload a new avatar image for the given target. The previous image (if any)
 * is deleted server-side in the same request; the flow is atomic from the
 * client's perspective.
 *
 * The file is resized client-side to a max 512px square JPEG before upload
 * so we don't pay to transfer a 10MB phone photo.
 */
export async function uploadAvatarImage(
  targetType: ProfileImageTargetType,
  targetId: string,
  file: File,
): Promise<UploadAvatarImageResult> {
  if (file.size === 0) throw new Error('Empty file');
  if (file.size > MAX_IMAGE_BYTES * 4) {
    // We'll downscale to ≤5MB, but reject absurdly large inputs up front
    // rather than loading 100MB into a canvas.
    throw new Error('Image too large (max 20MB)');
  }

  const processed = await resizeImageToJpeg(file, TARGET_MAX_DIMENSION);

  const form = new FormData();
  form.set('target_type', targetType);
  form.set('target_id', targetId);
  form.set('file', processed, processed.name);

  const { data, error } = await invokeFunction('profile-image-upload', {
    body: form,
  });
  if (error) throw error;
  return data as UploadAvatarImageResult;
}

export async function removeAvatarImage(
  targetType: ProfileImageTargetType,
  targetId: string,
): Promise<void> {
  const { error } = await invokeFunction('profile-image-remove', {
    body: { target_type: targetType, target_id: targetId },
  });
  if (error) throw error;
}

/**
 * Batch-resolve storage paths to signed URLs. Returns a map keyed by path;
 * paths the caller can't access or that no longer exist resolve to null.
 */
export async function getSignedAvatarUrls(
  paths: string[],
): Promise<SignedUrlBatchResult> {
  if (paths.length === 0) return { urls: {}, expires_in: 3600 };
  const { data, error } = await invokeFunction('profile-image-signed-url', {
    body: { paths },
  });
  if (error) throw error;
  return data as SignedUrlBatchResult;
}

/**
 * Downscale an image to a square JPEG sized to `maxDim` on the long side
 * (shorter side scaled proportionally). Returns a File so the caller can
 * hand it straight to FormData. Falls back to the original file if the
 * browser lacks canvas or decoding fails.
 */
async function resizeImageToJpeg(file: File, maxDim: number): Promise<File> {
  if (
    typeof document === 'undefined' ||
    typeof createImageBitmap !== 'function'
  ) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    const scale = Math.min(1, maxDim / Math.max(width, height));
    const targetW = Math.round(width * scale);
    const targetH = Math.round(height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close?.();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.85);
    });
    if (!blob) return file;

    return new File([blob], replaceExtension(file.name || 'avatar', 'jpg'), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch (err) {
    console.warn('[profile-image] resize failed, uploading original:', err);
    return file;
  }
}

function replaceExtension(name: string, ext: string): string {
  const idx = name.lastIndexOf('.');
  const base = idx > 0 ? name.slice(0, idx) : name;
  return `${base}.${ext}`;
}
